import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import type { OrderDetails } from '../../../services/orders.service';
import { computeOog, type OogComputeInput, type OogComputeResult } from '../../../services/oog.service';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { useCanApproveOogPermit, OOG_APPROVAL_COMPONENT_ID } from '../useCanApproveOogPermit';
import { CHASSIS_TYPES } from '../../../constants/orderFieldOptions';
import { formatMdYHm } from '../../../utils/dateUtils';

/**
 * Dimensions / OOG / Permits modal — port of V1's `equipspecs` popup
 * (triangle icon next to "Chass #"). No save call of its own: Save copies the
 * touched fields into the shared editedOrderFields buffer via updateOrderField
 * (exactly like V1's popup, which only copies into editrecordform and lets the
 * whole-form Save persist). Cancel discards local state.
 *
 * Portals to <body> at z-[130] above the Pro slide-over (z-[100]), matching
 * AccessorialEditModal's stacking convention.
 */

const INPUT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-[3px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100 dark:disabled:bg-gray-800';
const SELECT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 py-[2px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-gray-800';
const READ_CLS =
  'min-w-0 flex-1 bg-slate-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-[3px] ' +
  'text-[0.75rem] text-slate-600 dark:text-gray-400 h-6';

const YN = ['N', 'Y'] as const;

const COMPUTE_DEBOUNCE_MS = 250;

/** Right-aligned bold label + field, one row (AccessorialEditModal convention). */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center gap-2 py-0.5">
    <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
      {label}
    </label>
    <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
  </div>
);

// ── Pure helpers (exported for unit tests) ──────────────────────────────────

export function validateChassisSpecsSave(v: { chassisType: string; size: string }): string | null {
  const ct = (v.chassisType || '').toUpperCase();
  if (ct === 'SUPER LIGHT' && v.size !== '20') {
    return 'Size must be 20 to use a Super Light';
  }
  if (ct === 'ULTRA LIGHT' && v.size !== '40' && v.size !== '45') {
    return 'Size must be 40 or 45 to use an Ultra Light';
  }
  return null;
}

export function forceTriaxle(chassisType: string): 'Y' | 'N' {
  return (chassisType || '').toUpperCase().includes('TRIAXLE') ? 'Y' : 'N';
}

export interface OogLocalFlags {
  oog: string;
  brokerout: string;
  oversizePermitRequired: string;
  overweightPermitRequired: string;
}

/**
 * Resolves the four save-time compliance flags from the compute result, with
 * a safe fallback when `final` is null (debounce never resolved AND the
 * forced Save-time recompute also failed). On total compute failure we must
 * NOT silently default to 'N' — that would clobber a previously-saved 'Y'
 * (e.g. a legitimately required oversize permit) with "not required." Instead
 * fall back to the current local state, which reflects what's already on the
 * order / already shown in the UI.
 */
export function resolveOogFlags(
  final: OogComputeResult | null,
  local: OogLocalFlags,
): OogLocalFlags {
  if (!final) return local;
  return {
    oog: final.oog ? 'Y' : 'N',
    brokerout: final.brokerout ? 'Y' : 'N',
    oversizePermitRequired: final.oversizepermitrequired || 'N',
    overweightPermitRequired: final.overweightpermitrequired || 'N',
  };
}

export function inchesToFeetAndInches(inches: number): { feet: number; inches: number } {
  const total = Math.max(0, Math.round(inches || 0));
  return { feet: Math.floor(total / 12), inches: total % 12 };
}

export function inchesToCm(inches: number): number {
  return (inches || 0) * 2.54;
}

export function lbsToKgs(lbs: number): number {
  return (lbs || 0) * 0.453592;
}

export function kgsToLbs(kgs: number): number {
  return (kgs || 0) / 0.453592;
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  details: Partial<OrderDetails> | null;
  editedOrderFields: Record<string, unknown>;
  updateOrderField: (name: string, value: unknown) => void;
  currentUsername: string;
}

const ChassisSpecsModal: React.FC<Props> = ({
  open, onClose, details, editedOrderFields, updateOrderField, currentUsername,
}) => {
  const d = details ?? {};
  const canApprove = useCanApproveOogPermit();

  // Effective value: edited wins, then saved — seeded fresh each time the modal opens.
  const fv = <K extends keyof OrderDetails>(key: K): unknown =>
    editedOrderFields[key as string] !== undefined ? editedOrderFields[key as string] : (d as Partial<OrderDetails>)[key];

  const [lengthInches, setLengthInches] = useState(0);
  const [widthInches, setWidthInches] = useState(0);
  const [heightInches, setHeightInches] = useState(0);
  const [lbs, setLbs] = useState(0);
  const [kgs, setKgs] = useState(0);
  const [ooglegal, setOoglegal] = useState('N');
  const [chassisType, setChassisType] = useState('');
  const [oog, setOog] = useState('N');
  const [brokerout, setBrokerout] = useState('N');
  const [oversizePermitNumber, setOversizePermitNumber] = useState('');
  const [oversizePermitRequired, setOversizePermitRequired] = useState('N');
  const [oversizeApprovedBy, setOversizeApprovedBy] = useState('');
  const [oversizeApprovedDatetime, setOversizeApprovedDatetime] = useState('');
  const [overweightPermitNumber, setOverweightPermitNumber] = useState('');
  const [overweightPermitRequired, setOverweightPermitRequired] = useState('N');
  const [overweightApprovedBy, setOverweightApprovedBy] = useState('');
  const [overweightApprovedDatetime, setOverweightApprovedDatetime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed local state fresh every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setLengthInches(Number(fv('lengthInches')) || 0);
    setWidthInches(Number(fv('widthInches')) || 0);
    setHeightInches(Number(fv('heightInches')) || 0);
    setLbs(Number(fv('lbs')) || 0);
    setKgs(Number(fv('kgs')) || 0);
    setOoglegal(String(fv('ooglegal') ?? 'N'));
    setChassisType(String(fv('chassisType') ?? ''));
    setOog(String(fv('oog') ?? 'N'));
    setBrokerout(String(fv('brokerout') ?? 'N'));
    setOversizePermitNumber(String(fv('oversizePermitNumber') ?? ''));
    setOversizePermitRequired(String(fv('oversizePermitRequired') ?? 'N'));
    setOversizeApprovedBy(String(fv('oversizeApprovedBy') ?? ''));
    setOversizeApprovedDatetime(String(fv('oversizeApprovedDatetime') ?? ''));
    setOverweightPermitNumber(String(fv('overweightPermitNumber') ?? ''));
    setOverweightPermitRequired(String(fv('overweightPermitRequired') ?? 'N'));
    setOverweightApprovedBy(String(fv('overweightApprovedBy') ?? ''));
    setOverweightApprovedDatetime(String(fv('overweightApprovedDatetime') ?? ''));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Capture-phase Escape (AccessorialEditModal convention).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Builds the /oog/compute payload off the CURRENT local state. Shared by the
  // debounced live-preview effect and handleSave's final pre-save recompute so
  // the two call sites can't drift. arrivalUnchanged is always true: this modal
  // never edits legs, so whatever arrival is on the order is by definition
  // unchanged for the purposes of this compute.
  const buildOogComputePayload = (): OogComputeInput => ({
    size: String(fv('size') ?? ''),
    equipType: String(fv('equipType') ?? ''),
    chassisType,
    otr: String(fv('otr') ?? ''),
    lbs,
    lengthInches, widthInches, heightInches,
    moveType: String(d.moveType ?? ''),
    billTo: d.billTo ?? '',
    customer: d.customer ?? '',
    dispatcher: String(fv('dispatcher') ?? ''),
    isDaycab: String(fv('isDaycab') ?? ''),
    ooglegal,
    originateLeg: d.originateLeg ?? 0,
    deliveryLeg: d.deliveryLeg ?? 0,
    terminateLeg: d.terminateLeg ?? 0,
    legs: (d.legs ?? []).map((l) => ({
      legNum: l.legnum, origCode: l.origcode, destCode: l.destcode,
      legAction2: l.legAction2, arrivalDateTime: l.destArrivalDateTime,
      arrivalUnchanged: true,
    })),
  });

  // Debounced live recompute (mirrors checkfield_specs/oog_setfields).
  const [computed, setComputed] = useState<OogComputeResult | null>(null);
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (!open) return undefined;
    const reqId = ++reqIdRef.current;
    const handle = setTimeout(() => {
      computeOog(buildOogComputePayload()).then((r) => {
        if (reqId !== reqIdRef.current) return;
        setComputed(r);
        setOog(r.oog ? 'Y' : 'N');
        setBrokerout(r.brokerout ? 'Y' : 'N');
        setOversizePermitRequired(r.oversizepermitrequired || 'N');
        setOverweightPermitRequired(r.overweightpermitrequired || 'N');
      }).catch(() => { /* keep last computed on a network blip */ });
    }, COMPUTE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lengthInches, widthInches, heightInches, lbs, chassisType, ooglegal]);

  const feetLen = inchesToFeetAndInches(lengthInches);
  const feetWid = inchesToFeetAndInches(widthInches);
  const feetHt = inchesToFeetAndInches(heightInches);

  const toggleOversizeApproval = (checked: boolean) => {
    if (checked) {
      setOversizeApprovedBy(currentUsername);
      setOversizeApprovedDatetime(formatMdYHm(new Date()));
    } else {
      setOversizeApprovedBy('');
      setOversizeApprovedDatetime('');
    }
  };
  const toggleOverweightApproval = (checked: boolean) => {
    if (checked) {
      setOverweightApprovedBy(currentUsername);
      setOverweightApprovedDatetime(formatMdYHm(new Date()));
    } else {
      setOverweightApprovedBy('');
      setOverweightApprovedDatetime('');
    }
  };

  const handleSave = async () => {
    const size = String(fv('size') ?? '');
    const msg = validateChassisSpecsSave({ chassisType, size });
    if (msg) { setError(msg); return; }

    // Force one final, awaited recompute against the CURRENT local state before
    // writing anything — the debounced live-preview call (and `computed`, which
    // it feeds) may still be stale or in-flight, and the backend saverule does
    // NOT re-derive these flags on interactive saves (oog_checkspecs no-ops for
    // non-external saves; the frontend's /oog/compute is the source of truth
    // here). Bumping reqIdRef supersedes any pending debounce timer / in-flight
    // compute so a late-arriving stale result can't clobber this save's verdict.
    setSaving(true);
    reqIdRef.current += 1;
    let final = computed;
    try {
      final = await computeOog(buildOogComputePayload());
    } catch {
      /* Network blip on Save specifically — fall back to the last-known
         computed result rather than blocking the save, matching the live
         recompute effect's own catch-and-keep-last-result behavior. */
    }
    setSaving(false);

    // Never silently clear a permit-required flag on total compute failure
    // (debounce never resolved AND the forced recompute above also failed) —
    // fall back to the current local state rather than defaulting to 'N'.
    const flags = resolveOogFlags(final, { oog, brokerout, oversizePermitRequired, overweightPermitRequired });

    updateOrderField('lengthInches', lengthInches);
    updateOrderField('widthInches', widthInches);
    updateOrderField('heightInches', heightInches);
    updateOrderField('lbs', lbs);
    updateOrderField('kgs', kgs);
    updateOrderField('ooglegal', ooglegal);
    updateOrderField('chassisType', chassisType);
    updateOrderField('oog', flags.oog);
    updateOrderField('brokerout', flags.brokerout);
    updateOrderField('oversizePermitNumber', oversizePermitNumber);
    updateOrderField('oversizePermitRequired', flags.oversizePermitRequired);
    updateOrderField('oversizeApprovedBy', oversizeApprovedBy);
    updateOrderField('oversizeApprovedDatetime', oversizeApprovedDatetime);
    updateOrderField('overweightPermitNumber', overweightPermitNumber);
    updateOrderField('overweightPermitRequired', flags.overweightPermitRequired);
    updateOrderField('overweightApprovedBy', overweightApprovedBy);
    updateOrderField('overweightApprovedDatetime', overweightApprovedDatetime);
    updateOrderField('triaxle', forceTriaxle(chassisType));
    onClose();
  };

  // Alt+S saves, Alt+C closes — mirroring the footer buttons below, including
  // the `saving` guard. The layer keeps both off the Pro drawer behind this.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: open },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog" aria-modal="true" aria-label="Size Specifications"
      onClick={onClose} data-testid="chassis-specs-modal"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title="Size Specifications"
          onClose={onClose}
          closeLabel="Close size specifications"
          dragHandleProps={dragHandleProps}
        />

        <div className="overflow-y-auto px-4 py-3 text-[0.78125rem] text-slate-700 dark:text-gray-200">
          {/* Dimensions */}
          <h3 className="mb-1 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">Dimensions</h3>
          <Row label="Length">
            <input type="number" className={INPUT_CLS} aria-label="Length (inches)"
              value={lengthInches} onChange={(e) => setLengthInches(Number(e.target.value) || 0)} />
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">in.</span>
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">
              {feetLen.feet}ft {feetLen.inches}in · {inchesToCm(lengthInches).toFixed(0)}cm
            </span>
            <span className="shrink-0 text-[0.6875rem] font-semibold text-amber-700 dark:text-amber-400">
              {computed?.lengthOver ? `+${computed.lengthOver.toFixed(0)}in OOG` : ''}
            </span>
          </Row>
          <Row label="Width">
            <input type="number" className={INPUT_CLS} aria-label="Width (inches)"
              value={widthInches} onChange={(e) => setWidthInches(Number(e.target.value) || 0)} />
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">in.</span>
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">
              {feetWid.feet}ft {feetWid.inches}in · {inchesToCm(widthInches).toFixed(0)}cm
            </span>
            <span className="shrink-0 text-[0.6875rem] font-semibold text-amber-700 dark:text-amber-400">
              {computed?.widthOver ? `+${computed.widthOver.toFixed(0)}in OOG` : ''}
            </span>
          </Row>
          <Row label="Height">
            <input type="number" className={INPUT_CLS} aria-label="Height (inches)"
              value={heightInches} onChange={(e) => setHeightInches(Number(e.target.value) || 0)} />
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">in.</span>
            <span className="shrink-0 text-[0.6875rem] text-slate-500 dark:text-gray-400">
              {feetHt.feet}ft {feetHt.inches}in · {inchesToCm(heightInches).toFixed(0)}cm
            </span>
            <span className="shrink-0 text-[0.6875rem] font-semibold text-amber-700 dark:text-amber-400">
              {computed?.heightOver ? `+${computed.heightOver.toFixed(0)}in OOG` : ''}
            </span>
          </Row>

          <Row label="Legal">
            <select className={SELECT_CLS} aria-label="Legal" value={ooglegal}
              onChange={(e) => setOoglegal(e.target.value)}>
              {YN.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>

          {/* Weight */}
          <h3 className="mb-1 mt-2 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">Weight</h3>
          <Row label="Lbs / Kgs">
            <input type="number" className={INPUT_CLS} aria-label="Lbs" value={lbs}
              onChange={(e) => { const v = Number(e.target.value) || 0; setLbs(v); setKgs(Math.round(lbsToKgs(v))); }} />
            <input type="number" className={INPUT_CLS} aria-label="Kgs" value={kgs}
              onChange={(e) => { const v = Number(e.target.value) || 0; setKgs(v); setLbs(Math.round(kgsToLbs(v))); }} />
            <span className="shrink-0 text-[0.6875rem] font-semibold text-amber-700 dark:text-amber-400">
              {computed?.overLbs ? `+${computed.overLbs.toFixed(0)} lbs OOG` : ''}
            </span>
          </Row>

          {/* Chassis type / OOG / Broker Out */}
          <h3 className="mb-1 mt-2 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">Chassis</h3>
          <Row label="Chassis Type">
            <select className={SELECT_CLS} aria-label="Chassis type" value={chassisType}
              onChange={(e) => setChassisType(e.target.value)}>
              {(CHASSIS_TYPES as readonly string[]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Row>
          <Row label="OOG">
            <select className={SELECT_CLS} aria-label="OOG" value={oog} onChange={(e) => setOog(e.target.value)}>
              {YN.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
          <Row label="Broker Out">
            <select className={SELECT_CLS} aria-label="Broker Out" value={brokerout}
              onChange={(e) => setBrokerout(e.target.value)}>
              {YN.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>

          {/* Over Size Permit */}
          <h3 className="mb-1 mt-2 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">Over Size Permit</h3>
          <Row label="Permit #">
            <input type="text" className={INPUT_CLS} aria-label="Over Size Permit Number"
              value={oversizePermitNumber} onChange={(e) => setOversizePermitNumber(e.target.value)} />
          </Row>
          <Row label="Required">
            <select className={SELECT_CLS} aria-label="Over Size Permit Required" value={oversizePermitRequired}
              onChange={(e) => setOversizePermitRequired(e.target.value)}>
              {YN.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
          <PermissionsContainer componentId={OOG_APPROVAL_COMPONENT_ID} componentName="OOG Permit Approval" defaultDisabled>
            <Row label="Approve w/o Permit">
              <input type="checkbox" checked={!!oversizeApprovedBy} disabled={!canApprove}
                aria-label="Approve Over Size without Permit"
                onChange={(e) => toggleOversizeApproval(e.target.checked)} />
              <input type="text" className={READ_CLS} readOnly aria-label="Over Size Approved By" value={oversizeApprovedBy} />
              <input type="text" className={READ_CLS} readOnly aria-label="Over Size Approved Date" value={oversizeApprovedDatetime} />
            </Row>
          </PermissionsContainer>

          {/* Over Weight Permit */}
          <h3 className="mb-1 mt-2 text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">Over Weight Permit</h3>
          <Row label="Permit #">
            <input type="text" className={INPUT_CLS} aria-label="Over Weight Permit Number"
              value={overweightPermitNumber} onChange={(e) => setOverweightPermitNumber(e.target.value)} />
          </Row>
          <Row label="Required">
            <select className={SELECT_CLS} aria-label="Over Weight Permit Required" value={overweightPermitRequired}
              onChange={(e) => setOverweightPermitRequired(e.target.value)}>
              {YN.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Row>
          <PermissionsContainer
            componentId={OOG_APPROVAL_COMPONENT_ID} componentName="OOG Permit Approval"
            defaultDisabled showCog={false}
          >
            <Row label="Approve w/o Permit">
              <input type="checkbox" checked={!!overweightApprovedBy} disabled={!canApprove}
                aria-label="Approve Over Weight without Permit"
                onChange={(e) => toggleOverweightApproval(e.target.checked)} />
              <input type="text" className={READ_CLS} readOnly aria-label="Over Weight Approved By" value={overweightApprovedBy} />
              <input type="text" className={READ_CLS} readOnly aria-label="Over Weight Approved Date" value={overweightApprovedDatetime} />
            </Row>
          </PermissionsContainer>

          {error && <p className="mt-2 text-[0.75rem] font-semibold text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-2.5 dark:border-gray-700">
          <button type="button" onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="rounded bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChassisSpecsModal;
